'use client'

import { Suspense, useEffect, useRef, useState } from 'react'
import {
    BakeShadows,
    BBAnchor,
    Html,
    OrbitControls,
    PerspectiveCamera,
    PresentationControls,
    Shadow,
    Stats
} from '@react-three/drei'
import { useStation, useModel } from '@/app/store'
import { actionList } from '@/app/store/useModel'
import { sample } from '@/app/utils/functions'
import { Canvas } from '@react-three/fiber'
import BgImg from '@/app/(pages)/(home)/(components)/BgImg'
import BgSelector from '@/app/(pages)/(home)/(components)/BgSelector'
import Links from '@/app/(pages)/(home)/(components)/Links'
import Intro from '@/app/(pages)/(home)/(components)/Intro'
import LoadingIcon from '@/app/components/LoadingIcon'
import Message from '@/app/(pages)/(home)/(components)/Message'
import Model from '@/app/components/Model'
import classNames from 'classnames'

export default function Home() {

    const model = useModel()
    const stations = useStation().data
    const greetings = stations.map((s: any) => s.meta?.greet)?.flat()?.filter((g: any) => g)
    const [ greeting, setGreeting ] = useState(null)
    const [ clicked, setClicked ] = useState(false)
    const [ imgSrc, setImgSrc ] = useState(null)
    const audioRef = useRef<HTMLAudioElement>(null)
    const linksRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (model.actions) {
            model.playAction('standby')
            setGreeting(sample(greetings))
        }
    }, [model.actions])

    useEffect(() => {
        if (greeting && clicked) {
            audioRef.current.src = greeting.audio
            audioRef.current.play().catch((e) => {
                console.warn('Failed to play greeting audio:', e)
            })
        }
    }, [greeting])

    function handleGreeting() {
        if (!clicked) {
            setClicked(true)
            setGreeting({
                audio: '/sounds/narration/greeting.mp3',
                text: '嗨！我叫小鹿鹿，找到站牌圖標掃描，即可呼喚我一起完成任務，來獲得好玩點！'
            })
        }
        else {
            setGreeting(sample(greetings.filter((g: any) => g !== greeting)))
        }
    }

    return (
        <main className="relative flex-center flex-col justify-between h-full">
            <audio
                ref={audioRef}
                onPlay={() => {
                    model.playAction(sample(actionList.idle), null, true)
                }}
                onEnded={() => {
                    model.playAction('standby')
                }}
                onError={() => {
                    model.playAction('standby')
                }}
            />
            <div
                className="absolute top-0 flex-center w-screen max-w-[--content-max-width] rounded-b-[1.5rem] overflow-hidden z-[-1]"
                style={{height: `calc(100vh - ${linksRef.current?.clientHeight}px - 2rem + 1rem)`}}
            >
                <BgImg src={imgSrc} />
            </div>
            <div className="w-screen max-w-[--content-max-width] shrink-0 overflow-x-scroll">
                <BgSelector setImgSrc={setImgSrc} />
            </div>
            <div className="relative flex-center w-full h-full">
                {/* <div className="absolute flex-center w-[calc(100%+1rem)] h-[calc(100%+4rem)] rounded-[2rem] overflow-hidden border border-white border-2 z-[-1]">
                    <BackgroundImg src={imgSrc} />
                </div> */}
                <div className={classNames('absolute flex-center z-[2] pointer-events-none transition', {
                    'opacity-[0]': model.actions,
                    'opacity-[1]': !model.actions
                })}>
                    <LoadingIcon />
                </div>
                <Canvas
                    dpr={[1, 2]}
                    gl={{ preserveDrawingBuffer: false }} // 保留每一幀的渲染結果，將 3D 畫布導出成圖片時避免空白畫面，會耗效能
                    className={classNames('canvas h-full touch-none z-[1] transition duration-500', {
                        'opacity-[0]': !model.actions,
                        'opacity-[1]': model.actions
                    })}
                    onClick={handleGreeting}
                >
                    <Suspense>
                        <PerspectiveCamera makeDefault fov={15} near={0.5} position={[0, 0.75, 20]} />
                        {/* <PerspectiveCamera makeDefault position={[0, 0.75, 6]} /> */}
                        <PresentationControls
                            global
                            snap={true}
                            polar={[0, 0]}
                            azimuth={[-Infinity, Infinity]}
                        >
                            <Model>
                                <BBAnchor anchor={[0, 2.5, 0]}>
                                    <Html className="!relative max-w-[90%] min-w-[10rem] text-center" center>
                                        <Message>{greeting?.text}</Message>
                                    </Html>
                                </BBAnchor>
                                <Shadow scale={1.25} />
                            </Model>
                        </PresentationControls>
                        <BakeShadows />
                        <Model.Lights />
                        {/* <Stats showPanel={0} /> */}
                    </Suspense>
                </Canvas>
            </div>
            <div ref={linksRef} className="w-full">
                <Links />
            </div>
        </main>
    )
}
